<template>
  <SfModalPortal
    modal-id="budget-set-imports-modal"
    :eager="true"
    size="s"
    @close="close"
  >
    <template #title>
      {{ I18n.t('budget_set_import.upload_modal.title') }}
    </template>

    <template #messages>
      <SfModalMessage v-if="processingMessage" variant="info">
        <div class="sf-level sf-level-between sf-alert sf-alert-primary">
          <div class="sf-level-item">
            {{ processingMessage }}
          </div>
          <div class="sf-level-item sf-spinner sf-spinner-primary" />
        </div>
      </SfModalMessage>
    </template>

    <template #body>
      <div class="sf-below-3">
        <p>
          {{ I18n.t('budget_set_import.upload_modal.example_first_download') }}
          <a
            :href="xlsxFileExampleUrl"
            class="sf-a sf-c-primary"
            target="_blank"
          >
            {{ I18n.t('budget_set_import.upload_modal.example_link') }}
          </a>
          {{ I18n.t('budget_set_import.upload_modal.example_then_upload') }}
        </p>

        <p>
          {{ I18n.t('budget_set_import.upload_modal.message_uploading_data') }}
          <span class="sf-bold">{{ budgetSetName }}</span>
          {{ I18n.t('budget_set_import.upload_modal.message_for_period') }}
          <span class="sf-bold">{{ firstPeriod }}</span>
          {{ I18n.t('budget_set_import.upload_modal.message_to') }}
          <span class="sf-bold">{{ lastPeriod }}</span>

          <span v-if="hasAnalyticalDimensions">
            &nbsp;
            <span>
              {{ I18n.t('budget_set_import.upload_modal.message_analytical') }}
              <span class="sf-bold">{{ analyticalDimensionsSelected }}</span>
            </span> </span
          >.

          {{ I18n.t('budget_set_import.upload_modal.warning') }}
        </p>
      </div>

      <DirectUploadDropzone
        :accept="allowedFilenameExtensions"
        :on-file-added="handleFileAdded"
        :on-file-invalid="handleFileInvalid"
        :on-file-progress="handleFileProgress"
        :on-file-uploaded="handleFileUploaded"
      >
        <div class="sf-below-3 sf-bg-snow-light sf-layer-rounded sf-pad-3">
          <div class="sf-level">
            <span class="fa fa-upload" />
            <span
              v-dompurify-html="
                I18n.t('budget_set_import.upload_modal.drag_and_drop')
              "
              class="sf-align-c"
            />
          </div>
        </div>
      </DirectUploadDropzone>

      <div v-if="uploadedFile.file" class="sf-below-3">
        <div class="sf-tab">
          <div class="sf-tab-item sf-level sf-level-between">
            <div class="sf-level">
              <span
                v-if="validationStatus.errors"
                class="fa fa-solid fa-triangle-exclamation sf-c-danger-light-1"
                data-testid="budget-set-imports-modal-validation-status-icon-error"
              />
              <span
                v-else
                class="fa fa-regular fa-file"
                data-testid="budget-set-imports-modal-validation-status-icon-valid"
              />
              <div
                class="budget-imports__text--ellipsis"
                :class="{ 'sf-c-danger-light-1': validationStatus.errors }"
              >
                {{ fileName }}
              </div>
            </div>
            <div>
              <div
                v-if="!validationStatus.errors"
                class="
                  sf-progress sf-progress-tiny sf-bg-snow
                  budget-imports__progress
                "
              >
                <div
                  :style="{ width: uploadedFileProgress + '%' }"
                  class="sf-progress-complete sf-bg-primary"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <BudgetSetImportsModalValidationStatus
        :budget-set-name="budgetSetName"
        :validation-status="validationStatus"
        @close="close"
        @confirm="onOverwriteConfirm"
      />
    </template>
  </SfModalPortal>
</template>

<script setup lang="ts">
import type { Blob as ActiveBlob } from '@rails/activestorage'
import type { Ref } from 'vue'
import type { BudgetImportValidationStatus } from '../types'

import { ref, computed } from 'vue'

import BudgetSetImportsModalValidationStatus from './budget-set-imports-modal-validation-status.vue'
import { SfLog } from '@app/adapters/logger'
import * as api from '@app/application/budget/handlers/budget-set-imports-api'
import {
  SfModalPortal,
  SfModalMessage,
} from '@app/shared/vue/components/sf-modal'
import DirectUploadDropzone from '@app/shared/vue/components/direct-upload/direct-upload-dropzone.vue'

interface Props {
  budgetSetId: number
  budgetSetName: string
  entriesFilter: Record<string, string[]>
  firstPeriod: string
  lastPeriod: string
  hasAnalyticalDimensions: boolean
  analyticalDimensionsSelected: string
  allowedFilenameExtensions: string
  company: {
    firmId: string
    id: string
  }
}

const props = defineProps<Props>()
const emit = defineEmits(['close'])

const I18n = window.I18n

type UploadedFile = {
  file?: { name?: string }
  uuid?: string
  uploadProgress?: number
  invalidFile?: boolean
}

const processingMessage: Ref<string | null> = ref(null)
const budgetSetImportId: Ref<number> = ref(0)
const validationStatus: Ref<BudgetImportValidationStatus> = ref({})
const uploadedFile: Ref<UploadedFile> = ref({})

const xlsxFileExampleUrl = computed(() => {
  return window.Silverfin.Routes.download_xlsx_template_budget_set_path(
    props.company.firmId,
    props.company.id,
    props.budgetSetId,
    { ...props.entriesFilter, _options: true }
  )
})

const budgetSetShowPath = computed(() => {
  return window.Silverfin.Routes.budget_set_path(
    props.company.firmId,
    props.company.id,
    props.budgetSetId,
    { ...props.entriesFilter, _options: true }
  )
})

const fileName = computed(() => {
  return uploadedFile.value.file ? uploadedFile.value.file.name : ''
})
const uploadedFileProgress = computed(() => {
  return uploadedFile.value.uploadProgress
})

function handleFileException(e: unknown, message: string): void {
  processingMessage.value = null
  validationStatus.value = { errors: { invalidFile: true } }

  SfLog.info(message, {
    extra: {
      companyId: window.Silverfin.currentCompanyId,
      firmId: window.Silverfin.currentFirmId,
      error: e,
    },
  })
}

function handleFileInvalid({
  file,
  uuid,
}: {
  file: { name?: string }
  uuid: string
}): void {
  processingMessage.value = null
  uploadedFile.value = { file, uuid }
  validationStatus.value = { errors: { invalidFile: true } }
}

function handleFileAdded({
  file,
  uuid,
}: {
  file: { name?: string }
  uuid: string
}): void {
  processingMessage.value = null
  uploadedFile.value = { file, uuid, uploadProgress: 0 }
  validationStatus.value = {}
}

function handleFileProgress({
  uuid,
  progress,
}: {
  progress: string
  uuid: string
}): void {
  if (uploadedFile.value.uuid === uuid) {
    uploadedFile.value.uploadProgress = parseInt(progress)
  }
}

async function handleFileUploaded({
  blob,
  uuid,
}: {
  blob: ActiveBlob
  uuid: string
}): Promise<void> {
  if (uploadedFile.value.uuid === uuid) {
    processingMessage.value = I18n.t(
      'budget_set_import.upload_modal.processing'
    )

    const params = {
      budget_set_import: { ...props.entriesFilter, as_csv: blob.signed_id },
    }

    try {
      const response = await api.createBudgetSetImport(
        props.budgetSetId,
        params
      )

      if (response) {
        budgetSetImportId.value = response.id
        subscribe(response.messageBusLastId)
      }
    } catch (e) {
      handleFileException(e, 'Failed to upload budget set file')
    }
  }
}

function isValidationStatusEmptyOrValid(
  validationStatus: BudgetImportValidationStatus
): boolean {
  return (
    Object.entries(validationStatus).length === 0 || !!validationStatus.valid
  )
}

function subscribe(messageBusLastId: string | number): void {
  window.Silverfin.Polling.subscribe(
    '/budget_set_imports/processed',
    processed,
    messageBusLastId
  )
}

function unsubscribe(): void {
  window.Silverfin.Polling.unsubscribe(
    '/budget_set_imports/processed',
    processed
  )
}

async function processed(messageBusPayload: {
  budget_set_import_id: number
  validation_status: {
    valid: boolean
  }
}): Promise<void> {
  if (budgetSetImportId.value === messageBusPayload.budget_set_import_id) {
    unsubscribe()

    try {
      const response = (await api.getBudgetSetImport(
        props.budgetSetId,
        budgetSetImportId.value
      )) as {
        validationStatus: BudgetImportValidationStatus
      }

      validationStatus.value = response.validationStatus

      if (isValidationStatusEmptyOrValid(response.validationStatus)) {
        processingMessage.value = null
        window.Turbolinks.visit(budgetSetShowPath.value)
      } else {
        processingMessage.value = null
      }
    } catch (e) {
      handleFileException(e, 'Failed to process budget upload')
    }
  }
}

async function onOverwriteConfirm(): Promise<void> {
  validationStatus.value = {}
  processingMessage.value = I18n.t('budget_set_import.upload_modal.overwriting')

  const response = await api.updateBudgetSetImport(
    props.budgetSetId,
    budgetSetImportId.value
  )

  if (response) {
    subscribe(response.messageBusLastId)
  }
}

function close(): void {
  processingMessage.value = null
  budgetSetImportId.value = 0
  validationStatus.value = {}
  uploadedFile.value = {}
  emit('close')
}
</script>

<style scoped>
.budget-imports__text--ellipsis {
  text-overflow: ellipsis;
  overflow: hidden;
  width: 180px;
}

.budget-imports__progress {
  width: 50px;
}
</style>
